/**
 * PaymentFormModal Component
 * 
 * Modal de formulário de pagamento
 * - Tokenização de cartão (PCI DSS compliant)
 * - Validação de campos
 * - Loading states
 * - Retry automático se token expirar
 * 
 * Fase 5 - UI de Pagamentos
 */

import { useState } from 'react';
import type { FormEvent } from 'react';
import { X, CreditCard, Loader2, Shield, Lock } from 'lucide-react';
import { PaymentService } from '../../services/PaymentService';
import { isValidCPF } from '../../utils/validateCPF';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { PaymentResultModal } from './PaymentResultModal';
import type { PaymentResultType } from './PaymentResultModal';
import { supabase } from '../../lib/supabase';

interface PaymentFormModalProps {
  plan: 'basic' | 'premium';
  billingCycle: 'monthly' | 'semester' | 'annual' | 'test3d';
  immediateDowngrade?: boolean; // Caso B: Downgrade imediato Premium → Basic
  onClose: () => void;
}

export function PaymentFormModal({ plan, billingCycle, immediateDowngrade, onClose }: PaymentFormModalProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  // Método de pagamento (fixo em credit_card)
  const paymentMethod = 'credit_card' as const;

  // Estado do formulário
   
  const [_cardBrand, _setCardBrand] = useState<'visa' | 'mastercard' | 'amex' | 'elo' | 'hipercard' | null>(null);
  const [cardData, setCardData] = useState({
    number: '',
    holder: '',
    expiry: '',
    cvv: ''
  });

  const [customerData, setCustomerData] = useState({
    name: user?.user_metadata?.full_name || '',
    email: user?.email || '',
    document: '',
    phone: '',
    zip_code: '',
    address: '',
    city: '',
    state: ''
  });

  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [showResult, setShowResult] = useState<PaymentResultType | null>(null);
  const [resultMessage, setResultMessage] = useState<string>('');
  const [canCloseProcessing, setCanCloseProcessing] = useState<boolean>(false);

  // Validações
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Handler de mudança de cartão com formatação
  function handleCardChange(field: string, value: string) {
    let formatted = value;

    if (field === 'number') {
      formatted = PaymentService.formatCardNumber(value);
    } else if (field === 'expiry') {
      formatted = PaymentService.formatExpiry(value);
    } else if (field === 'cvv') {
      formatted = value.replace(/\D/g, '').slice(0, 4);
    }

    setCardData(prev => ({ ...prev, [field]: formatted }));

    // Limpar erro deste campo
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  }

  // Validar formulário
  function validateForm(): boolean {
    const newErrors: Record<string, string> = {};

    // Validar cartão
    if (!PaymentService.validateCardNumber(cardData.number)) {
      newErrors.number = 'Número de cartão inválido';
    }

    // Validar expiração
    const [month, year] = cardData.expiry.split('/');
    if (!PaymentService.validateExpiry(month, year)) {
      newErrors.expiry = 'Data de expiração inválida';
    }

    // Validar CVV
    if (!PaymentService.validateCVV(cardData.cvv)) {
      newErrors.cvv = 'CVV inválido';
    }

    // Validar nome
    if (!cardData.holder.trim()) {
      newErrors.holder = 'Nome do titular é obrigatório';
    }

    // Validar CPF (document)
    const cpfClean = (customerData.document || '').replace(/\D/g, '');
    if (!cpfClean) {
      newErrors.document = 'CPF obrigatório';
    } else if (cpfClean.length < 11) {
      newErrors.document = 'CPF incompleto';
    } else if (!isValidCPF(cpfClean)) {
      newErrors.document = 'CPF inválido';
    }

    if (!customerData.phone) {
      newErrors.phone = 'Telefone é obrigatório';
    }

    if (!customerData.zip_code || customerData.zip_code.length < 8) {
      newErrors.zip_code = 'CEP inválido';
    }

    if (!customerData.address.trim()) {
      newErrors.address = 'Endereço é obrigatório';
    }

    if (!customerData.city.trim()) {
      newErrors.city = 'Cidade é obrigatória';
    }

    if (!customerData.state.trim() || customerData.state.length !== 2) {
      newErrors.state = 'Estado é obrigatório (2 letras)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  // Aguardar confirmação quando retornar 'pending' (webhook)
  async function waitForPaymentResult(
    timeoutMs: number = 45000,
    intervalMs: number = 1500
  ): Promise<'paid' | 'failed' | 'cancelled' | 'timeout'> {
    const start = Date.now();
    try {
      while (Date.now() - start < timeoutMs) {
        // 1) Sucesso: assinatura ativa e plano != free
        const { data: sub } = await supabase
          .from('user_subscriptions')
          .select('plan_tier, status')
          .eq('user_id', user!.id)
          .single();

        if (sub && sub.status === 'active' && sub.plan_tier !== 'free') {
          return 'paid';
        }

        // 2) Falha/Cancelado: avaliar transações recentes (últimos 2 minutos) com precedência
        const sinceIso = new Date(Date.now() - 2 * 60 * 1000).toISOString();
        const { data: txList } = await supabase
          .from('payment_transactions')
          .select('status, created_at')
          .eq('user_id', user!.id)
          .gte('created_at', sinceIso)
          .order('created_at', { ascending: false })
          .limit(5);

        if (Array.isArray(txList) && txList.length > 0) {
          const statuses = (txList as any[]).map(t => t.status);
          if (statuses.includes('paid')) return 'paid';
          if (statuses.includes('refunded')) return 'cancelled';
          if (statuses.includes('failed')) return 'failed';
        }

        await new Promise((r) => setTimeout(r, intervalMs));
      }
      return 'timeout';
    } catch (e) {
      console.error('Erro no polling de confirmação de pagamento:', e);
      return 'timeout';
    }
  }

  // Submeter formulário
  async function handleSubmit(e: FormEvent) {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Obter plan_id (UUID da tabela subscription_plans no Supabase)
      const planIds = {
        basic: '41e55d64-22ad-4b1c-a22e-4698ec3795d2',
        premium: '671c1f10-2bbe-4a56-a8d3-e54553326316'
      };
      const plan_id = planIds[plan];

      // PASSO 1: Tokenizar cartão
      setLoadingMessage('🔒 Processando dados do cartão de forma segura...');
      const tokenStartTime = Date.now();

      const [month, year] = cardData.expiry.split('/');

      const cardToken = await PaymentService.tokenizeCard({
        number: cardData.number,
        holder_name: cardData.holder,
        exp_month: month,
        exp_year: `20${year}`, // Converter YY para YYYY
        cvv: cardData.cvv
      });

      // Verificar se token ainda é válido (< 55s)
      let finalToken = cardToken;
      const tokenAge = Date.now() - tokenStartTime;

      if (tokenAge > 55000) {
        console.warn('Token próximo de expirar, recriando...');
        setLoadingMessage('⏱️ Atualizando token de segurança...');

        finalToken = await PaymentService.tokenizeCard({
          number: cardData.number,
          holder_name: cardData.holder,
          exp_month: month,
          exp_year: `20${year}`,
          cvv: cardData.cvv
        });
      }

      // PASSO 2: Criar assinatura
      setLoadingMessage('✨ Criando sua assinatura...');

      const payload = {
        plan_id,
        billing_cycle: billingCycle,
        payment_method: paymentMethod,
        ...(immediateDowngrade && { immediate_downgrade: true }), // Caso B: Downgrade imediato
        customer: {
          name: customerData.name,
          email: customerData.email,
          document: customerData.document.replace(/\D/g, ''),
          phone: customerData.phone.replace(/\D/g, ''),
          address: {
            line_1: customerData.address,
            zip_code: customerData.zip_code.replace(/\D/g, ''),
            city: customerData.city,
            state: customerData.state,
            country: 'BR'
          }
        },
        payment_data: {
          card_token: finalToken
        }
      };

      const resp = await PaymentService.createSubscription(payload);

      // Encerrar loading
      setLoading(false);

      // NOVO: Verificar se é downgrade agendado
      if (resp?.scheduled === true) {
        // Downgrade agendado - mostrar sucesso com mensagem específica
        setShowResult('success');
        const effectiveDate = resp.effective_at 
          ? new Date(resp.effective_at).toLocaleDateString('pt-BR') 
          : 'na próxima renovação';
        setResultMessage(
          resp.message || 
          `Downgrade agendado para ${effectiveDate}. Você continuará com acesso ao plano atual até lá.`
        );
        // Aguardar 4s e redirecionar
        setTimeout(() => {
          navigate('/profile?tab=payment');
        }, 4000);
        return; // Sair da função
      }

      // Decidir fluxo pelo status retornado pela Edge Function
      if (resp?.status === 'active') {
        // SUCESSO imediato (capturado na criação)
        setShowResult('success');
        const cycleNames = {
          monthly: 'Mensal',
          semester: 'Semestral',
          annual: 'Anual',
          test3d: '3 dias (Teste)'
        };
        setResultMessage(
          `Sua assinatura do plano ${planNames[plan]} (${cycleNames[billingCycle]}) foi ativada com sucesso!`
        );
        // Aguardar 3s e redirecionar
        setTimeout(() => {
          navigate('/dashboard');
        }, 3000);
      } else {
        // Pagamento em processamento (aguardar confirmação por webhook)
        setShowResult('processing');
        setResultMessage('Pagamento em processamento. Aguarde a autenticação do seu banco (3D Secure). Verifique SMS, app do banco ou token para confirmar a compra.');
        setCanCloseProcessing(false);

        // Iniciar polling para atualizar modal automaticamente
        (async () => {
          const outcome = await waitForPaymentResult();
          if (outcome === 'paid') {
            setShowResult('success');
            const cycleNames = {
              monthly: 'Mensal',
              semester: 'Semestral',
              annual: 'Anual',
              test3d: '3 dias (Teste)'
            };
            setResultMessage(
              `Sua assinatura do plano ${planNames[plan]} (${cycleNames[billingCycle]}) foi ativada com sucesso!`
            );
            setTimeout(() => {
              navigate('/dashboard');
            }, 3000);
          } else if (outcome === 'failed') {
            setShowResult('error_failed');
            setResultMessage('Pagamento não aprovado. Verifique os dados do cartão.');
            setCanCloseProcessing(false);
          } else if (outcome === 'cancelled') {
            setShowResult('cancelled');
            setResultMessage('Transação cancelada/estornada. Nenhuma cobrança será realizada.');
            setCanCloseProcessing(false);
          } else {
            // timeout: orientar usuário
            setResultMessage('Pagamento em processamento. Se o banco solicitou confirmação (3D Secure), aprove no SMS/app do seu banco. Você pode fechar este modal e verificar seu plano em Perfil > Pagamento. Caso não confirme, a operação poderá ser recusada.');
            setCanCloseProcessing(true);
          }
        })();
      }

    } catch (err: any) {
      console.error('Erro ao processar pagamento:', err);
      setLoading(false);

      // Tratar diferentes tipos de erro
      let errorMsg = 'Erro ao processar pagamento. Tente novamente.';
      let errorType: PaymentResultType = 'error_failed';
      
      // 🔒 VALIDAÇÃO DE PAGAMENTO (erro 402 = Payment Required)
      if (err.message.includes('não aprovado') || err.message.includes('not approved')) {
        // Tentar extrair detalhes do erro
        const statusMatch = err.message.match(/status[:"'\s]+(\w+)/i);
        const messageMatch = err.message.match(/message[:"'\s]+([^"'}]+)/i);
        
        const status = statusMatch ? statusMatch[1] : 'unknown';
        const acquirerMsg = messageMatch ? messageMatch[1].trim() : '';
        
        // Mapear status para mensagens amigáveis
        if (status === 'not_authorized') {
          errorMsg = 'Cartão recusado pelo banco emissor. Tente outro cartão ou entre em contato com seu banco.';
          if (acquirerMsg) errorMsg += `\n\nDetalhes: ${acquirerMsg}`;
          errorType = 'error_declined';
        } else if (status === 'failed' || status === 'with_error') {
          errorMsg = 'Falha ao processar pagamento. Verifique os dados e tente novamente.';
          if (acquirerMsg) errorMsg += `\n\nDetalhes: ${acquirerMsg}`;
          errorType = 'error_failed';
        } else if (status === 'waiting_capture' || status === 'waiting_cancellation') {
          errorMsg = 'Pagamento em processamento. Aguarde alguns instantes e tente novamente.';
          errorType = 'processing';
        } else {
          errorMsg = acquirerMsg || 'Pagamento não aprovado. Verifique os dados do cartão.';
          errorType = 'error_failed';
        }
      } else if (/expir|expired|timeout/i.test(err.message)) {
        // Ajuste: só considerar expiração de TOKEN quando o texto mencionar "token" explicitamente.
        // Evita confundir mensagens como "card expiration date is invalid" com expiração do token.
        const tokenExpiredRegex = /(token).*(expir|expired|timeout)|\bexpired\s+token\b|\btoken\s+expired\b|\btoken\b.*\btimeout\b|\btimeout\b.*\btoken\b/i;
        if (tokenExpiredRegex.test(err.message)) {
          errorMsg = 'Token de segurança expirou. Por favor, tente novamente.';
          errorType = 'error_failed';
        } else {
          // Mensagem genérica mantendo o conteúdo real do erro
          errorMsg = err.message || 'Erro ao processar pagamento. Tente novamente.';
          errorType = 'error_failed';
        }
      } else if (/card_id,?\s*card_token\s*or\s*card\s*field\s*is\s*required/i.test(err.message)) {
        // Erro de integração: payload sem um dos campos obrigatórios
        errorMsg = 'Falha ao processar pagamento: token/cartão ausente. Tente novamente.';
        errorType = 'error_failed';
      } else if (err.message.includes('recusado') || err.message.includes('declined')) {
        errorMsg = 'Cartão recusado pelo banco emissor. Tente outro cartão ou entre em contato com seu banco.';
        errorType = 'error_declined';
      } else if (err.message.includes('insuficiente') || err.message.includes('insufficient')) {
        errorMsg = 'Saldo insuficiente. Tente outro cartão.';
        errorType = 'error_declined';
      } else if (err.message.includes('inválido') || err.message.includes('invalid')) {
        errorMsg = 'Dados do cartão inválidos. Verifique e tente novamente.';
        errorType = 'error_failed';
      } else if (err.message) {
        errorMsg = err.message;
        errorType = 'error_failed';
      }

      setResultMessage(errorMsg);
      setShowResult(errorType);
    }
  }

  const planNames = {
    basic: 'Básico',
    premium: 'Premium'
  };

  // Handler para retry
  function handleRetry() {
    setShowResult(null);
    setResultMessage('');
    setError(null);
    setCanCloseProcessing(false);
  }

  // Handler para fechar modal de sucesso
  function handleSuccessClose() {
    navigate('/dashboard');
  }

  // Se estiver mostrando resultado, renderizar modal de resultado
  if (showResult) {
    // Modal de processamento com botão X (apenas após timeout)
    if (showResult === 'processing' && canCloseProcessing) {
      return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl relative">
            <button
              aria-label="Fechar"
              className="absolute top-3 right-3 p-2 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              onClick={() => {
                setShowResult(null);
                setCanCloseProcessing(false);
              }}
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
            <div className="p-8 text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-50 rounded-full mb-6">
                <Loader2 className="w-12 h-12 text-blue-500 animate-spin" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">⏳ Processando Pagamento</h2>
              <p className="text-gray-600 leading-relaxed">{resultMessage}</p>
            </div>
          </div>
        </div>
      );
    }

    // Demais tipos usam o componente padrão
    return (
      <PaymentResultModal
        type={showResult}
        message={resultMessage}
        onClose={showResult === 'success' ? handleSuccessClose : () => setShowResult(null)}
        onRetry={(showResult === 'error_declined' || showResult === 'error_failed' || showResult === 'cancelled') ? handleRetry : undefined}
      />
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto relative">
        {/* Loading Overlay */}
        {loading && (
          <div className="absolute inset-0 bg-white/95 z-50 flex flex-col items-center justify-center">
            <Loader2 className="w-12 h-12 animate-spin text-blue-600 mb-4" />
            <p className="text-lg font-semibold text-gray-800 text-center px-4">
              {loadingMessage}
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Aguarde, processando de forma segura...
            </p>
          </div>
        )}

        {/* Header */}
        <div className="sticky top-0 bg-white border-b p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Finalizar Assinatura</h2>
            <p className="text-gray-600">Plano {planNames[plan]}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            disabled={loading}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Erro Geral */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <div className="text-red-600">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="font-semibold text-red-800">Erro no pagamento</p>
                <p className="text-sm text-red-600">{error}</p>
              </div>
            </div>
          )}

          {/* Dados do Cartão */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <CreditCard className="w-5 h-5 text-blue-600" />
              <h3 className="text-lg font-semibold">Dados do Cartão</h3>
            </div>

            <div className="space-y-4">
              {/* Número do Cartão */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Número do Cartão
                </label>
                <input
                  type="text"
                  value={cardData.number}
                  onChange={(e) => handleCardChange('number', e.target.value)}
                  placeholder="0000 0000 0000 0000"
                  maxLength={19}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.number ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={loading}
                />
                {errors.number && <p className="text-sm text-red-600 mt-1">{errors.number}</p>}
              </div>

              {/* Nome do Titular */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome do Titular
                </label>
                <input
                  type="text"
                  value={cardData.holder}
                  onChange={(e) => handleCardChange('holder', e.target.value.toUpperCase())}
                  placeholder="COMO ESTÁ NO CARTÃO"
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.holder ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={loading}
                />
                {errors.holder && <p className="text-sm text-red-600 mt-1">{errors.holder}</p>}
              </div>

              {/* Expiração e CVV */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Validade
                  </label>
                  <input
                    type="text"
                    value={cardData.expiry}
                    onChange={(e) => handleCardChange('expiry', e.target.value)}
                    placeholder="MM/AA"
                    maxLength={5}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.expiry ? 'border-red-500' : 'border-gray-300'
                    }`}
                    disabled={loading}
                  />
                  {errors.expiry && <p className="text-sm text-red-600 mt-1">{errors.expiry}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    CVV
                  </label>
                  <input
                    type="text"
                    value={cardData.cvv}
                    onChange={(e) => handleCardChange('cvv', e.target.value)}
                    placeholder="123"
                    maxLength={4}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.cvv ? 'border-red-500' : 'border-gray-300'
                    }`}
                    disabled={loading}
                  />
                  {errors.cvv && <p className="text-sm text-red-600 mt-1">{errors.cvv}</p>}
                </div>
              </div>
            </div>
          </div>

          {/* Dados do Titular */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Dados Pessoais</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">CPF</label>
                <input
                  type="text"
                  value={customerData.document}
                  onChange={(e) => setCustomerData(prev => ({ ...prev, document: e.target.value }))}
                  onBlur={() => {
                    const cpf = (customerData.document || '').replace(/\D/g, '');
                    let msg = '';
                    if (!cpf) msg = 'CPF obrigatório';
                    else if (cpf.length < 11) msg = 'CPF incompleto';
                    else if (!isValidCPF(cpf)) msg = 'CPF inválido';
                    setErrors(prev => ({ ...prev, document: msg }));
                  }}
                  placeholder="000.000.000-00"
                  className={`w-full px-4 py-3 border rounded-lg ${errors.document ? 'border-red-500' : 'border-gray-300'}`}
                  disabled={loading}
                />
                {errors.document && <p className="text-sm text-red-600 mt-1">{errors.document}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
                <input
                  type="text"
                  value={customerData.phone}
                  onChange={(e) => setCustomerData(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="(00) 00000-0000"
                  className={`w-full px-4 py-3 border rounded-lg ${errors.phone ? 'border-red-500' : 'border-gray-300'}`}
                  disabled={loading}
                />
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">CEP</label>
                <input
                  type="text"
                  value={customerData.zip_code}
                  onChange={(e) => setCustomerData(prev => ({ ...prev, zip_code: e.target.value }))}
                  placeholder="00000-000"
                  className={`w-full px-4 py-3 border rounded-lg ${errors.zip_code ? 'border-red-500' : 'border-gray-300'}`}
                  disabled={loading}
                />
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Endereço</label>
                <input
                  type="text"
                  value={customerData.address}
                  onChange={(e) => setCustomerData(prev => ({ ...prev, address: e.target.value }))}
                  placeholder="Rua, número, complemento"
                  className={`w-full px-4 py-3 border rounded-lg ${errors.address ? 'border-red-500' : 'border-gray-300'}`}
                  disabled={loading}
                  required
                />
                {errors.address && <p className="text-sm text-red-500 mt-1">{errors.address}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cidade</label>
                <input
                  type="text"
                  value={customerData.city}
                  onChange={(e) => setCustomerData(prev => ({ ...prev, city: e.target.value }))}
                  placeholder="São Paulo"
                  className={`w-full px-4 py-3 border rounded-lg ${errors.city ? 'border-red-500' : 'border-gray-300'}`}
                  disabled={loading}
                  required
                />
                {errors.city && <p className="text-sm text-red-500 mt-1">{errors.city}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                <input
                  type="text"
                  value={customerData.state}
                  onChange={(e) => setCustomerData(prev => ({ ...prev, state: e.target.value.toUpperCase() }))}
                  placeholder="SP"
                  maxLength={2}
                  className={`w-full px-4 py-3 border rounded-lg ${errors.state ? 'border-red-500' : 'border-gray-300'}`}
                  disabled={loading}
                  required
                />
                {errors.state && <p className="text-sm text-red-500 mt-1">{errors.state}</p>}
              </div>
            </div>
          </div>

          {/* Trust Badges */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center gap-3 text-sm text-blue-800">
              <Shield className="w-5 h-5" />
              <div>
                <p className="font-semibold">Pagamento 100% Seguro</p>
                <p className="text-xs text-blue-600">
                  Seus dados são protegidos com criptografia PCI DSS Level 1
                </p>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold text-lg hover:from-blue-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Processando...
              </>
            ) : (
              <>
                <Lock className="w-5 h-5" />
                Finalizar Assinatura
              </>
            )}
          </button>

          <p className="text-xs text-center text-gray-500">
            Ao finalizar, você concorda com nossos Termos de Uso e Política de Privacidade
          </p>
        </form>
      </div>
    </div>
  );
}
